import { Tile } from './types';
import type { PublicState, Theme, Point } from './types';

/**
 * The board.
 *
 * A CSS grid of cells, as in 2025 -- not a canvas. Keeping the DOM means the
 * visual tuning carries over as CSS, tokens can animate between squares for
 * free, and the whole thing re-themes through custom properties.
 *
 * What changes is where the knowledge lives. The 2025 markup was generated by
 * PHP with room names baked in, and the client re-derived which room you were
 * standing in by proximity to an anchor. Here the grid is built from the theme
 * and every cell knows its room, so nothing has to be guessed.
 */

const cellKey = (p: Point) => `${p.x},${p.y}`;

export interface BoardCallbacks {
  onCellClick?: (point: Point) => void;
}

/** Milliseconds a token spends crossing one square. */
const MS_PER_SQUARE = 150;

/** However long the route, a single move should not outstay its welcome. */
const MAX_WALK_MS = 2200;

export class Board {
  private readonly root: HTMLElement;
  private readonly theme: Theme;
  private readonly cells = new Map<string, HTMLElement>();
  private readonly roomOf = new Map<string, string>();
  private reachable = new Set<string>();

  /** Tokens live in their own layer so they can move independently of cells. */
  private tokenLayer!: HTMLElement;
  private readonly tokens = new Map<number, HTMLElement>();

  /** Where each token was last drawn, so movement can be animated from it. */
  private readonly lastSeen = new Map<number, Point>();

  private readonly running = new Map<number, Animation>();

  constructor(root: HTMLElement, theme: Theme, private readonly callbacks: BoardCallbacks = {}) {
    this.root = root;
    this.theme = theme;

    for (const room of theme.rooms) {
      for (const cell of [...room.cells, ...room.doors]) {
        this.roomOf.set(cellKey(cell), room.id);
      }
    }

    this.build();
  }

  /** Which room owns a square, or null. O(1), and never a guess. */
  roomAt(p: Point): string | null {
    return this.roomOf.get(cellKey(p)) ?? null;
  }

  private tileAt(p: Point): number {
    const { cols, rows, tiles } = this.theme.board;
    if (p.x < 0 || p.y < 0 || p.x >= cols || p.y >= rows) return Tile.Void;
    return tiles[p.y]?.[p.x] ?? Tile.Void;
  }

  /**
   * Is a single step legal? Mirrors Theme::canStep on the server.
   *
   * Shared by reachability highlighting and by the walking animation, so a
   * token never takes a route the player could not.
   */
  private canStep(a: Point, b: Point): boolean {
    const ta = this.tileAt(a);
    const tb = this.tileAt(b);
    if (ta === Tile.Void || tb === Tile.Void) return false;

    if (ta === Tile.Corridor) return tb === Tile.Corridor || tb === Tile.Door;
    if (ta === Tile.Door) {
      if (tb === Tile.Corridor) return true;
      return this.roomAt(a) !== null && this.roomAt(a) === this.roomAt(b);
    }
    return this.roomAt(a) !== null && this.roomAt(a) === this.roomAt(b);
  }

  private build(): void {
    const { cols, rows, tiles, underlayImage } = this.theme.board;

    this.root.innerHTML = '';
    this.root.style.setProperty('--cols', String(cols));
    this.root.style.setProperty('--rows', String(rows));

    // A theme with no board art yet keeps today's flat tile colours -- only
    // themes that opt in with underlayImage get transparent cells, so lab
    // (which has none) is untouched by this.
    //
    // Set directly as an inline style, not through a CSS custom property: a
    // relative url() stored in a custom property resolves against whichever
    // stylesheet consumes it via var(), not against the document -- so a
    // rule in the compiled styles.css (served from /assets/) would resolve
    // this relative to /assets/ instead of the page itself, breaking the path.
    if (underlayImage) {
      this.root.style.backgroundImage = `url("themes/${this.theme.id}/${underlayImage}")`;
      this.root.style.backgroundSize = '100% 100%';
      this.root.classList.add('has-underlay');
    }

    for (let y = 0; y < rows; y++) {
      for (let x = 0; x < cols; x++) {
        const tile = tiles[y]?.[x] ?? Tile.Void;
        const cell = document.createElement('div');

        cell.className = `cell t-${tile}`;
        cell.dataset.x = String(x);
        cell.dataset.y = String(y);

        const roomId = this.roomOf.get(`${x},${y}`);
        if (roomId) {
          cell.dataset.room = roomId;
        }

        if (tile !== Tile.Void) {
          cell.addEventListener('click', () => this.callbacks.onCellClick?.({ x, y }));
        }

        this.cells.set(`${x},${y}`, cell);
        this.root.appendChild(cell);
      }
    }

    this.tokenLayer = document.createElement('div');
    this.tokenLayer.className = 'token-layer';
    this.root.appendChild(this.tokenLayer);

    this.addLabels();
  }

  /**
   * Room names, positioned over their anchor and nudged by the theme's
   * labelOffset -- the same offX/offY the 2025 config carried, now data.
   */
  private addLabels(): void {
    const { cols, rows } = this.theme.board;

    const place = (text: string, anchor: Point, offset: Point, extra = '') => {
      const label = document.createElement('div');
      label.className = `room-label${extra}`;
      label.textContent = text;
      label.style.left = `calc(${((anchor.x + 0.5) / cols) * 100}% + ${offset.x}px)`;
      label.style.top = `calc(${((anchor.y + 0.5) / rows) * 100}% + ${offset.y}px)`;
      this.root.appendChild(label);
    };

    for (const room of this.theme.rooms) {
      place(room.name, room.anchor, room.labelOffset);
    }

    const centre = this.theme.board.centerpiece;
    if (centre) {
      // The case file and the Accuse button stack inside the centrepiece room,
      // so they need to know where it is. They used to be pinned to a flat
      // `top: 40%`, which was a guess that survived until the board gained
      // artwork and turned out to sit above the room's ceiling. The anchor is
      // the theme's own statement of where the middle of that room is, and it
      // is the same fact the label already uses -- so publish it once here and
      // let the stack hang off it, rather than have three magic numbers drift
      // apart the next time a board is redrawn.
      this.root.style.setProperty('--centre-y', `${((centre.anchor.y + 0.5) / rows) * 100}%`);

      // Below the button, not under it: the label is the least urgent thing in
      // the room and the only one that can be shouldered aside.
      place(centre.label, centre.anchor, { x: 0, y: 48 }, ' centre');
    }
  }

  /**
   * Redraw tokens and highlighting for a state.
   *
   * Cheap enough to run on every update: the grid itself is built once and
   * only the token layer and a set of classes change.
   */
  render(state: PublicState, mySeat: number | null): void {
    for (const [key, cell] of this.cells) {
      cell.classList.toggle('reachable', this.reachable.has(key));
    }

    // How many tokens share each square, so they can be nudged apart.
    const crowd = new Map<string, number>();
    const slot = new Map<number, number>();
    state.players.forEach((seat, index) => {
      const key = `${seat.x},${seat.y}`;
      const n = crowd.get(key) ?? 0;
      slot.set(index, n);
      crowd.set(key, n + 1);
    });

    state.players.forEach((seat, index) => {
      const suspect = this.theme.suspects.find((s) => s.id === seat.id);
      let token = this.tokens.get(index);

      if (!token) {
        token = document.createElement('div');
        token.className = 'token';
        token.textContent = suspect?.token ?? '?';
        token.title = seat.name;
        token.style.width = `${100 / this.theme.board.cols}%`;
        token.style.height = `${100 / this.theme.board.rows}%`;
        this.tokenLayer.appendChild(token);
        this.tokens.set(index, token);
      }

      token.classList.toggle('is-out', seat.hasLost);
      token.classList.toggle('is-turn', index === state.turnIndex && state.phase !== 'gameover');
      token.classList.toggle('is-me', index === mySeat);

      const shared = (crowd.get(`${seat.x},${seat.y}`) ?? 1) > 1;
      const nudge = shared ? (slot.get(index) === 0 ? -18 : 18) : 0;
      token.style.transform = `translate(${nudge}%, ${nudge}%)`;
      token.style.zIndex = String(100 + (slot.get(index) ?? 0));

      this.place(index, token, { x: seat.x, y: seat.y });
    });
  }

  /**
   * Put a token where it belongs -- walking it there if it has moved.
   *
   * The server plays a bot's whole turn inside one request, so by the time the
   * client sees anything the bot has already finished moving. Without this the
   * token simply appears somewhere else and the move is impossible to follow.
   */
  private place(index: number, token: HTMLElement, to: Point): void {
    const from = this.lastSeen.get(index);
    this.lastSeen.set(index, to);

    const settle = () => {
      token.style.left = `${(to.x / this.theme.board.cols) * 100}%`;
      token.style.top = `${(to.y / this.theme.board.rows) * 100}%`;
    };

    const stillness = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;

    if (!from || (from.x === to.x && from.y === to.y) || stillness) {
      settle();
      return;
    }

    // A move already in flight is abandoned rather than queued: the newest
    // state is the truth, and stacking animations would drift further behind
    // the game with every turn.
    this.running.get(index)?.cancel();

    const route = this.findPath(from, to);
    if (route.length < 2) {
      settle();
      return;
    }

    const frames = route.map((p) => ({
      left: `${(p.x / this.theme.board.cols) * 100}%`,
      top: `${(p.y / this.theme.board.rows) * 100}%`,
    }));

    const animation = token.animate(frames, {
      duration: Math.min((route.length - 1) * MS_PER_SQUARE, MAX_WALK_MS),
      easing: 'ease-in-out',
      fill: 'none',
    });

    // Set the final position immediately so that if the animation is cancelled
    // -- or never runs, as with reduced motion -- the token is still correct.
    settle();
    this.running.set(index, animation);
    animation.finished.catch(() => undefined).finally(() => this.running.delete(index));
  }

  /**
   * The squares a token walks through to get from one place to another.
   *
   * Uses the same legality rules as movement, so a token rounds corners and
   * goes through doorways instead of sliding across walls.
   */
  findPath(from: Point, to: Point): Point[] {
    const start = cellKey(from);
    const goal = cellKey(to);
    if (start === goal) return [from];

    const cameFrom = new Map<string, Point>();
    const seen = new Set<string>([start]);
    const queue: Point[] = [from];

    while (queue.length) {
      const current = queue.shift()!;

      for (const next of [
        { x: current.x, y: current.y - 1 },
        { x: current.x, y: current.y + 1 },
        { x: current.x - 1, y: current.y },
        { x: current.x + 1, y: current.y },
      ]) {
        const key = cellKey(next);
        if (seen.has(key) || !this.canStep(current, next)) continue;

        seen.add(key);
        cameFrom.set(key, current);

        if (key === goal) {
          const route: Point[] = [next];
          let cursor = key;
          while (cameFrom.has(cursor)) {
            const previous = cameFrom.get(cursor)!;
            route.unshift(previous);
            cursor = cellKey(previous);
          }
          return route;
        }

        queue.push(next);
      }
    }

    // Secret passages have no walkable route; the token simply appears.
    return [];
  }

  setReachable(cells: Iterable<Point>): void {
    this.reachable = new Set([...cells].map(cellKey));
    for (const [key, cell] of this.cells) {
      cell.classList.toggle('reachable', this.reachable.has(key));
    }
  }

  clearReachable(): void {
    this.setReachable([]);
  }

  /**
   * Squares reachable with the steps available.
   *
   * Same rules as the server: you enter and leave a room through its doors,
   * and moving between tiles of one room is free. Recomputing it here is what
   * lets the highlight appear instantly instead of after a round trip -- the
   * server still validates the move that follows.
   */
  computeReachable(from: Point, steps: number): Point[] {
    const tileAt = (p: Point) => this.tileAt(p);
    const canStep = (a: Point, b: Point) => this.canStep(a, b);

    const cost = (a: Point, b: Point): number =>
      tileAt(a) === Tile.Room && tileAt(b) === Tile.Room && this.roomAt(a) === this.roomAt(b) ? 0 : 1;

    const best = new Map<string, number>([[cellKey(from), steps]]);
    const queue: Point[] = [from];
    const found: Point[] = [];

    while (queue.length) {
      const current = queue.shift()!;
      const left = best.get(cellKey(current))!;

      for (const next of [
        { x: current.x, y: current.y - 1 },
        { x: current.x, y: current.y + 1 },
        { x: current.x - 1, y: current.y },
        { x: current.x + 1, y: current.y },
      ]) {
        if (!canStep(current, next)) continue;

        const remaining = left - cost(current, next);
        if (remaining < 0) continue;

        const key = cellKey(next);
        if ((best.get(key) ?? -1) >= remaining) continue;

        best.set(key, remaining);
        queue.push(next);
        if (next.x !== from.x || next.y !== from.y) {
          found.push(next);
        }
      }
    }

    // De-duplicate; a square can be enqueued more than once as better routes
    // to it are discovered.
    const seen = new Set<string>();
    return found.filter((p) => {
      const key = cellKey(p);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }
}
